"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { useWaveDropsLeaderboard } from "@/hooks/useWaveDropsLeaderboard";
import React from "react";
import { WaveLeaderboardDrop } from "./WaveLeaderboardDrop";
import { WaveLeaderboardEmptyState } from "./WaveLeaderboardEmptyState";
import { WaveLeaderboardLoading } from "./WaveLeaderboardLoading";
import { WaveLeaderboardLoadingBar } from "./WaveLeaderboardLoadingBar";
interface WaveLeaderboardDropsProps {
  readonly wave: ApiWave;
  readonly sort: WaveDropsLeaderboardSort;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly onCreateDrop?: (() => void) | undefined;
  readonly curatedByGroupId?: string | undefined;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly priceCurrency?: string | undefined;
}
export const WaveLeaderboardDrops: React.FC<WaveLeaderboardDropsProps> = t0 => {
  const $ = _c(39);
  const {
    wave,
    sort,
    isVotingClosed: t1,
    isVotingControlsLocked: t2,
    onDropClick,
    onCreateDrop,
    curatedByGroupId,
    minPrice,
    maxPrice,
    priceCurrency
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const isVotingControlsLocked = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== curatedByGroupId || $[1] !== maxPrice || $[2] !== minPrice || $[3] !== priceCurrency || $[4] !== sort || $[5] !== wave.id) {
    t3 = {
      waveId: wave.id,
      sort,
      curatedByGroupId,
      minPrice,
      maxPrice,
      priceCurrency
    };
    $[0] = curatedByGroupId;
    $[1] = maxPrice;
    $[2] = minPrice;
    $[3] = priceCurrency;
    $[4] = sort;
    $[5] = wave.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useWaveDropsLeaderboard(t3);
  let t4;
  if ($[7] !== refetch) {
    t4 = () => {
      refetch();
    };
    $[7] = refetch;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleSourceDropDeleted = t4;
  let t5;
  if ($[9] !== fetchNextPage || $[10] !== hasNextPage || $[11] !== isFetching || $[12] !== isFetchingNextPage) {
    t5 = async () => {
      if (hasNextPage && !isFetching && !isFetchingNextPage) {
        await fetchNextPage();
      }
    };
    $[9] = fetchNextPage;
    $[10] = hasNextPage;
    $[11] = isFetching;
    $[12] = isFetchingNextPage;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const intersectionElementRef = useIntersectionObserver(t5);
  if (isFetching && drops.length === 0) {
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <WaveLeaderboardLoading />;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  if (drops.length === 0) {
    let t6;
    if ($[15] !== onCreateDrop || $[16] !== wave) {
      t6 = <WaveLeaderboardEmptyState onCreateDrop={onCreateDrop} wave={wave} />;
      $[15] = onCreateDrop;
      $[16] = wave;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  let t6;
  if ($[18] !== drops || $[19] !== handleSourceDropDeleted || $[20] !== isVotingClosed || $[21] !== isVotingControlsLocked || $[22] !== onDropClick || $[23] !== wave) {
    let t7;
    if ($[25] !== handleSourceDropDeleted || $[26] !== isVotingClosed || $[27] !== isVotingControlsLocked || $[28] !== onDropClick || $[29] !== wave) {
      t7 = drop => <WaveLeaderboardDrop key={drop.id} drop={drop} wave={wave} onDropClick={onDropClick} onSourceDropDeleted={handleSourceDropDeleted} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
      $[25] = handleSourceDropDeleted;
      $[26] = isVotingClosed;
      $[27] = isVotingControlsLocked;
      $[28] = onDropClick;
      $[29] = wave;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    t6 = drops.map(t7);
    $[18] = drops;
    $[19] = handleSourceDropDeleted;
    $[20] = isVotingClosed;
    $[21] = isVotingControlsLocked;
    $[22] = onDropClick;
    $[23] = wave;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[31] !== isFetchingNextPage) {
    t7 = isFetchingNextPage && <WaveLeaderboardLoadingBar />;
    $[31] = isFetchingNextPage;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== intersectionElementRef) {
    t8 = <div ref={intersectionElementRef} />;
    $[33] = intersectionElementRef;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t6 || $[36] !== t7 || $[37] !== t8) {
    t9 = <div className="tw-space-y-4">{t6}{t7}{t8}</div>;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
};